import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { Typography, Button, Spin } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import SuggestedProductBanner from './SuggestedProductBanner'
import SuggestedProductBannerModal from './SuggestedProductBannerModal'
import { searchSuggestedProductsByClient } from '../../actions'
import { trackAnalyticsCommerceEvent, buildItemListName } from '../../Utils/AnalyticsUtils'

const { Title } = Typography
const CARD_GAP = 16
const AUTO_SCROLL_MS = 4000
const GA_LABEL = 'Producto sugerido - banner'

class SuggestedProductsCarousel extends Component {
  constructor(props) {
    super(props)
    this.scrollContainerRef = React.createRef()
    this.autoScrollTimer = null
    this.state = { currentIndex: 0, shuffledProducts: [], canScroll: false, selectedProduct: null }
  }

  componentDidMount() {
    this.props.searchSuggestedProductsByClient({ displayLocation: this.props.displayLocation })
    window.addEventListener('resize', this.checkScrollable)
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientSearch !== prevProps.clientSearch) {
      const products = this.props.clientSearch?.data || []
      const shuffled = [...products].sort(() => Math.random() - 0.5)
      this.setState({ shuffledProducts: shuffled }, () => {
        this.checkScrollable()
        this.clearAutoScroll()
        if (shuffled.length >= 3) this.startAutoScroll()
      })
    }
  }

  componentWillUnmount() {
    this.clearAutoScroll()
    window.removeEventListener('resize', this.checkScrollable)
  }

  checkScrollable = () => {
    const el = this.scrollContainerRef.current
    if (!el) return
    this.setState({ canScroll: el.scrollWidth > el.clientWidth })
  }

  getCardWidth = () => (this.props.compact ? 136 : this.props.isMobile ? 160 : 192)

  // Portrait banner: width:height ratio ~3:4 (final banner size still to be defined by graphics team)
  getCardHeight = () => Math.round((this.getCardWidth() * 4) / 3)

  handleBannerClick = (suggestedProduct) => {
    const { displayLocation, user } = this.props
    const codigodearticulo = suggestedProduct.product?.codigodearticulo
    // GA4 only parses the `items` array into its own item-scoped fields (visible as such
    // in DebugView) for its reserved ecommerce event names — a made-up event name like
    // "view_suggested_banner" gets `items` sent as an opaque, unparsed parameter instead.
    // Using "view_item" (rather than "select_item") also wires this straight into GA4's
    // built-in view_item -> add_to_cart ecommerce funnel/conversion reports, filterable
    // by item_list_name, with no custom Exploration needed.
    trackAnalyticsCommerceEvent(
      'view_item',
      GA_LABEL,
      codigodearticulo,
      user,
      null,
      [
        {
          item_id: codigodearticulo,
          item_name: suggestedProduct.product?.descripcion,
          item_list_name: buildItemListName({ source: 'suggested_product_banner', displayLocation }),
        },
      ],
    )
    this.setState({ selectedProduct: suggestedProduct })
  }

  handleCloseBannerModal = () => {
    this.setState({ selectedProduct: null })
  }

  scrollToIndex = (index) => {
    const el = this.scrollContainerRef.current
    if (!el) return
    el.scrollTo({ left: index * (this.getCardWidth() + CARD_GAP), behavior: 'smooth' })
    this.setState({ currentIndex: index })
  }

  autoAdvance = () => {
    const { shuffledProducts } = this.state
    const next = this.state.currentIndex === shuffledProducts.length - 1 ? 0 : this.state.currentIndex + 1
    this.scrollToIndex(next)
  }

  handleScrollLeft = () => {
    const { shuffledProducts } = this.state
    const next = this.state.currentIndex === 0 ? shuffledProducts.length - 1 : this.state.currentIndex - 1
    this.scrollToIndex(next)
    this.resetAutoScroll()
  }

  handleScrollRight = () => {
    const { shuffledProducts } = this.state
    const next = this.state.currentIndex === shuffledProducts.length - 1 ? 0 : this.state.currentIndex + 1
    this.scrollToIndex(next)
    this.resetAutoScroll()
  }

  startAutoScroll = () => {
    this.autoScrollTimer = setInterval(this.autoAdvance, AUTO_SCROLL_MS)
  }

  clearAutoScroll = () => {
    if (this.autoScrollTimer) clearInterval(this.autoScrollTimer)
  }

  resetAutoScroll = () => {
    this.clearAutoScroll()
    this.startAutoScroll()
  }

  render() {
    const { isLoadingClientSearch, isMobile, t } = this.props
    const { shuffledProducts: products, canScroll } = this.state

    if (isLoadingClientSearch) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      )
    }

    if (products.length === 0) return null

    const cardWidth = this.getCardWidth()
    const cardHeight = this.getCardHeight()

    return (
      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <Title level={isMobile ? 4 : 3} style={{ marginBottom: 24, textAlign: 'left' }}>
          {t('You may also be interested in')}
        </Title>

        <div style={{ position: 'relative' }}>
          {!isMobile && canScroll && (
            <>
              <Button
                icon={<LeftOutlined />}
                onClick={this.handleScrollLeft}
                shape='circle'
                size='large'
                style={{
                  position: 'absolute',
                  left: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              />
              <Button
                icon={<RightOutlined />}
                onClick={this.handleScrollRight}
                shape='circle'
                size='large'
                style={{
                  position: 'absolute',
                  right: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              />
            </>
          )}

          <div
            ref={this.scrollContainerRef}
            style={{
              display: 'flex',
              gap: CARD_GAP,
              overflowX: 'hidden',
              overflowY: 'hidden',
              paddingBottom: 16,
            }}
          >
            {products.map((suggestedProduct) => (
              <div
                key={suggestedProduct.id || suggestedProduct.product.codigodearticulo}
                style={{ minWidth: cardWidth, maxWidth: cardWidth, height: cardHeight }}
              >
                <SuggestedProductBanner suggestedProduct={suggestedProduct} onClick={this.handleBannerClick} />
              </div>
            ))}
          </div>
        </div>

        <SuggestedProductBannerModal
          suggestedProduct={this.state.selectedProduct}
          displayLocation={this.props.displayLocation}
          visible={!!this.state.selectedProduct}
          onClose={this.handleCloseBannerModal}
        />
      </div>
    )
  }
}

const mapStateToProps = ({ suggestedProducts, auth }, ownProps) => {
  const key = ownProps.displayLocation || 'default'
  return {
    clientSearch: suggestedProducts.clientSearch[key],
    isLoadingClientSearch: suggestedProducts.isLoadingClientSearch[key],
    user: auth.user.user,
  }
}

export default connect(mapStateToProps, { searchSuggestedProductsByClient })(
  withTranslation()(SuggestedProductsCarousel),
)
